import { Injectable, inject } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, concatMap, catchError } from 'rxjs/operators';

import {
  LoadDataByIdAction,
  DataByIdLoadedSuccessAction,
  DataByIdLoadedErrorAction,
  AllDataLoadedSuccessAction,
  AllDataLoadedErrorAction,
  LoadAllDataAction,
  AddDataAction,
  AddDataSuccessAction,
  AddDataFailedAction,
  EditDataAction,
  EditDataSuccessAction,
  EditDataFailedAction,
  DeleteDataAction,
  DeleteDataSuccessAction,
  DeleteDataFailedAction,
  ImportADDataAction,
  ImportADDataFailedAction,
  ImportADDataSuccessAction,
  RefreshADDataAction,
  RefreshADDataFailedAction,
  RefreshADDataSuccessAction,
  ImportDataFailedAction,
  ImportDataSuccessAction,
  ImportDataAction,
  ImportUsersDataAction,
  ImportUsersDataSuccessAction,
  ImportUsersDataFailedAction,
  DeleteUsersDataSuccessAction,
  DeleteUsersDataFailedAction,
  DeleteUsersDataAction,
  SendPinDataAction,
  SendPinDataSuccessAction,
  SendPinDataFailedAction,
  AccountEditDataAction,
  AccountEditSuccessAction,
  AccountEditDataFailedAction,
} from '../actions/data.actions';
import { DataService } from '../services';
import { DataCommandResponse, DataEntity } from '../models/data.model';

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return String(error);
}

function assertSuccessfulResponse(response: DataCommandResponse): void {
  if (!response.success) {
    throw new Error(`${response.errorCode ?? 'Error'} - ${response.errorDescription ?? ''}`.trim());
  }
}

@Injectable()
export class DataEffects {
  private dataService = inject(DataService);
  private actions$ = inject(Actions);

  addData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddDataAction),
      concatMap((action) => {
        return this.dataService.addData<DataCommandResponse>(action.dataType, action.payload).pipe(
          map((x: DataCommandResponse) => {
            assertSuccessfulResponse(x);
            return AddDataSuccessAction({
              dataType: action.dataType,
              id: String(x.data.id),
              commandId: action.commandId,
              success: true,
              data: x.data,
            });
          }),
          catchError((error: unknown) => {
            return [
              AddDataFailedAction({
                dataType: action.dataType,
                error: toErrorMessage(error),
                commandId: action.commandId,
                success: false,
              }),
            ];
          })
        );
      })
    )
  );

  importData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImportDataAction),
      concatMap((action) => {
        return this.dataService.importData<DataCommandResponse>(action.dataType, action.payload).pipe(
          map((x: DataCommandResponse) => {
            assertSuccessfulResponse(x);
            return ImportDataSuccessAction({
              dataType: action.dataType,
              id: String(x.data.id),
              commandId: action.commandId,
              success: true,
            });
          }),
          catchError((error: unknown) => {
            return [
              ImportDataFailedAction({
                dataType: action.dataType,
                error: toErrorMessage(error),
                commandId: action.commandId,
                success: false,
              }),
            ];
          })
        );
      })
    )
  );

  editData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditDataAction),
      concatMap((action) => {
        return this.dataService.editData<DataCommandResponse>(action.id, action.dataType, action.payload).pipe(
          map((x: DataCommandResponse) => {
            assertSuccessfulResponse(x);
            return EditDataSuccessAction({
              dataType: action.dataType,
              id: String(x.data.id),
              commandId: action.commandId,
              success: true,
              data: x.data,
            });
          }),
          catchError((error: unknown) => {
            return [
              EditDataFailedAction({
                id: action.id,
                dataType: action.dataType,
                error: toErrorMessage(error),
                commandId: action.commandId,
                success: false,
              }),
            ];
          })
        );
      })
    )
  );

  deleteData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteDataAction),
      concatMap((action) => {
        return this.dataService.deleteData<DataCommandResponse>(action.dataType, action.payload).pipe(
          map((x: DataCommandResponse) => {
            assertSuccessfulResponse(x);
            return DeleteDataSuccessAction({
              dataType: action.dataType,
              id: String(x.data.id),
              commandId: action.commandId,
              success: true,
            });
          }),
          catchError((error: unknown) => {
            return [
              DeleteDataFailedAction({
                dataType: action.dataType,
                error: toErrorMessage(error),
                commandId: action.commandId,
                success: false,
              }),
            ];
          })
        );
      })
    )
  );

  /*refreshAllDataAfterSuccessFullDelet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteDataSuccessAction),
            map(action => LoadAllDataAction({
                dataType: action.dataType
            }))
        ));*/

  loaDataById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadDataByIdAction),
      concatMap((action) => {
        return this.dataService.fetchDataById<DataEntity>(action.dataType, action.id).pipe(
          map((x: DataEntity) => DataByIdLoadedSuccessAction({ id: action.id, dataType: action.dataType, data: x })),
          catchError((error: unknown) => {
            return [
              DataByIdLoadedErrorAction({ id: action.id, dataType: action.dataType, error: toErrorMessage(error) }),
            ];
          })
        );
      })
    )
  );

  loadAllDataByType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadAllDataAction),
      concatMap((action) => {
        return this.dataService.fetchDataByType<DataEntity>(action.dataType).pipe(
          map((x) => {
            return AllDataLoadedSuccessAction({ dataType: action.dataType, data: x.data ?? [] });
          }),
          catchError((error: unknown) => {
            return [AllDataLoadedErrorAction({ dataType: action.dataType, error: toErrorMessage(error) })];
          })
        );
      })
    )
  );

  /* ***** Active Deirectory ***** */
  importADData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImportADDataAction),
      concatMap((action) => {
        return this.dataService.importADData<DataCommandResponse>(action.dataType, action.payload).pipe(
          map((x: DataCommandResponse) => {
            assertSuccessfulResponse(x);
            return ImportADDataSuccessAction({
              dataType: action.dataType,
              id: String(x.data.id),
              commandId: action.commandId,
              success: true,
            });
          }),
          catchError((error: unknown) => {
            return [
              ImportADDataFailedAction({
                dataType: action.dataType,
                error: toErrorMessage(error),
                commandId: action.commandId,
                success: false,
              }),
            ];
          })
        );
      })
    )
  );

  refreshADData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RefreshADDataAction),
      concatMap((action) => {
        return this.dataService.refreshADData<DataCommandResponse>(action.dataType, action.payload).pipe(
          map((x: DataCommandResponse) => {
            assertSuccessfulResponse(x);
            return RefreshADDataSuccessAction({
              dataType: action.dataType,
              id: String(x.data.id),
              commandId: action.commandId,
              success: true,
            });
          }),
          catchError((error: unknown) => {
            return [
              RefreshADDataFailedAction({
                dataType: action.dataType,
                error: toErrorMessage(error),
                commandId: action.commandId,
                success: false,
              }),
            ];
          })
        );
      })
    )
  );

  /* ***** Import ***** */
  importUsersData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImportUsersDataAction),
      concatMap((action) => {
        return this.dataService.importUsersData<DataCommandResponse>(action.id, action.dataType, action.payload).pipe(
          map((x: DataCommandResponse) => {
            assertSuccessfulResponse(x);
            return ImportUsersDataSuccessAction({
              dataType: action.dataType,
              id: String(x.data.id),
              commandId: action.commandId,
              success: true,
            });
          }),
          catchError((error: unknown) => {
            return [
              ImportUsersDataFailedAction({
                dataType: action.dataType,
                error: toErrorMessage(error),
                commandId: action.commandId,
                success: false,
              }),
            ];
          })
        );
      })
    )
  );

  /* ***** Import ***** */
  deleteUsersData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteUsersDataAction),
      concatMap((action) => {
        return this.dataService.deleteUsersData<DataCommandResponse>(action.id, action.dataType, action.payload).pipe(
          map((x: DataCommandResponse) => {
            assertSuccessfulResponse(x);
            return DeleteUsersDataSuccessAction({
              dataType: action.dataType,
              id: String(x.data.id),
              commandId: action.commandId,
              success: true,
            });
          }),
          catchError((error: unknown) => {
            return [
              DeleteUsersDataFailedAction({
                dataType: action.dataType,
                error: toErrorMessage(error),
                commandId: action.commandId,
                success: false,
              }),
            ];
          })
        );
      })
    )
  );

  sendPinData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SendPinDataAction),
      concatMap((action) => {
        return this.dataService.sendPinData<DataCommandResponse>(action.dataType, action.payload).pipe(
          map((x: DataCommandResponse) => {
            assertSuccessfulResponse(x);
            return SendPinDataSuccessAction({
              dataType: action.dataType,
              id: String(x.data.id),
              commandId: action.commandId,
              success: true,
            });
          }),
          catchError((error: unknown) => {
            return [
              SendPinDataFailedAction({
                dataType: action.dataType,
                error: toErrorMessage(error),
                commandId: action.commandId,
                success: false,
              }),
            ];
          })
        );
      })
    )
  );

  accountEditData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountEditDataAction),
      concatMap((action) => {
        return this.dataService.accountEditData<DataCommandResponse>(action.dataType, action.payload).pipe(
          map((x: DataCommandResponse) => {
            assertSuccessfulResponse(x);
            return AccountEditSuccessAction({
              dataType: action.dataType,
              commandId: action.commandId,
              success: true,
            });
          }),
          catchError((error: unknown) => {
            return [
              AccountEditDataFailedAction({
                dataType: action.dataType,
                error: toErrorMessage(error),
                commandId: action.commandId,
                success: false,
              }),
            ];
          })
        );
      })
    )
  );
}
